import React, { useState, useMemo, useEffect } from "react";
import { Box, Typography, CircularProgress, Paper, IconButton } from "@mui/material";
import { tokens } from "@/tokens";
import { useGetScenarioCardsQuery, useCalculateMetricsQuery, ScenarioCard } from "@/services/dashboard/sm-api";
import ScenarioList from "./components/scenario-list";
import ScenarioDetail from "./components/scenario-detail";
import CreateScenarioForm from "./components/create-scenario";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import EditIcon from "@/assets/svgs/edit.svg";
import DeleteIcon from "@/assets/svgs/garbage.svg";
import CloseIcon from "@mui/icons-material/Close";

const ScenarioModelingPage: React.FC = () => {
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient, areClientsLoading } = useAppSelector((state) => state.client);
  const { selectedTenant,areTenantsLoading } = useAppSelector((state) => state.tenantManagement);

  // Clear selected scenario when client changes
  useEffect(() => {
    setSelectedScenario(null);
  }, [selectedClient?._id]);

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  // For client accounts, use the user's own id as the clientId (backend expects client/user ObjectId)
  const isClientAccount = useMemo(() => {
    return user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT;
  }, [user]);

  const isAdmin = useMemo(() => {
    return user?.userRole === roles.TDADMIN;
  }, [user, selectedTenant]);

  const isTDAdmin = useMemo(() => {
    return isAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const commonOptions = useMemo(() => {
    if (isTDAdmin) {
      return {
        skip: !selectedTenant,
        refetchOnMountOrArgChange: true,
      };
    }
    return {
      skip: canSelectClients && !selectedClient,
      refetchOnMountOrArgChange: true,
    };
  }, [canSelectClients, selectedClient]);

  // Get the clientId, ensuring it's not an empty string
  // For client accounts, use the user's _id
  // For SP accounts, use the selectedClient._id
  const clientId = useMemo(() => {
    if (isClientAccount && user?._id) {
      return user._id;
    }
    if(isTDAdmin && selectedTenant?._id){
      return selectedTenant?._id;
    }
    return selectedClient?._id || undefined;
  }, [isClientAccount, user, selectedClient, selectedTenant]);

  // Debug logs
  useEffect(() => {
    console.log("User role:", user?.userRole);
    console.log("Is client account:", isClientAccount);
    console.log("User tenantId:", user?.tenantId);
    console.log("Selected client:", selectedClient);
    console.log("Using clientId:", clientId);
  }, [user, isClientAccount, selectedClient, clientId]);

  const { data: scenariosData,isFetching: isFetchingScenarios, error: scenariosError } = useGetScenarioCardsQuery(
    { 
      clientId: clientId || "",
      ...(isAdmin && { tenantId: selectedTenant?._id }),
      pageNo: 1,
      pageSize: 10
    },
    {
      ...commonOptions,
      // Skip the query if there's no clientId
      skip: isTDAdmin ? false : commonOptions.skip || !clientId
    }
  );

  // Debug logs for API response
  useEffect(() => {
    console.log("Scenarios data:", scenariosData);
    console.log("Scenarios error:", scenariosError);
  }, [scenariosData, scenariosError]);

  const { data: metricsData, isLoading: isLoadingMetrics, refetch: refetchMetrics } = useCalculateMetricsQuery(
    { scenarioId: selectedScenario ?? "" },
    { skip: !selectedScenario }
  );

  const handleScenarioDeleted = () => {
    setSelectedScenario(null);
  };

  if (areClientsLoading || isFetchingScenarios || areTenantsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (scenariosError) {
    return (
      <Box p={3}>
        <Typography color="error">Error loading scenarios</Typography>
      </Box>
    );
  }

  const scenarios = scenariosData?.scenarioCards.data || [];
  const selectedScenarioData = scenarios.find(s => s._id === selectedScenario);

  return (
          <Box>
      <Box mb={2}>
        <CreateScenarioForm
          onScenarioCreated={(scenario: ScenarioCard) => setSelectedScenario(scenario._id)}
          clientId={clientId}
          isAdmin={isAdmin}
          tenantId={selectedTenant?._id}
        />
      </Box>

      <Box mb={2}>
        <ScenarioList
          scenarios={scenarios}
          selectedScenarioId={selectedScenario}
          onScenarioSelect={setSelectedScenario}
        />
      </Box>

      {selectedScenarioData && (
        <Box mt={0}>
          {/* Duty Increase Details Box with buttons */}
          <Paper
            elevation={0}
            sx={{
              p: "12px 14px",
              borderRadius: tokens.radius.lg,
              border: `1px solid ${tokens.border.default}`,
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, flexWrap: "wrap" }}>
                <Typography sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, fontSize: tokens.fontSize.lg }}>
                  Duty Increase
                </Typography>
                {isLoadingMetrics ? (
                  <CircularProgress size={16} />
                ) : metricsData?.calculateScenarioMetrics ? (
                  <Typography sx={{ color: tokens.status.danger, fontWeight: tokens.fontWeight.bold, fontSize: "1.5rem", lineHeight: 1 }}>
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(metricsData.calculateScenarioMetrics.dutyAmount)}
                  </Typography>
                ) : null}
                <Typography sx={{ color: tokens.text.excerpt, fontSize: tokens.fontSize.sm2 }}>
                  Rate: <span style={{ color: tokens.status.danger }}>{selectedScenarioData.rate}%</span> · Countries: {selectedScenarioData.country_list.join(", ")}
                  {selectedScenarioData.hts_codes.length > 0 && (
                    <> · HTS: {selectedScenarioData.hts_codes.length > 2 ? `${selectedScenarioData.hts_codes.slice(0, 2).join(", ")}...` : selectedScenarioData.hts_codes.join(", ")}</>
                  )}
                  {selectedScenarioData.dateRange && (
                    <> · {new Date(selectedScenarioData.dateRange.start).getFullYear() === 1999
                      ? "All Dates"
                      : `${new Date(selectedScenarioData.dateRange.start).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} – ${new Date(selectedScenarioData.dateRange.end).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                    }</>
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
                <IconButton
                  onClick={() => setIsEditDialogOpen(true)}
                  size="small"
                  sx={{ color: tokens.text.excerpt, p: 0.5 }}
                >
                  <img src={EditIcon} alt="Edit" style={{ width: 18, height: 18 }} />
                </IconButton>
                <IconButton
                  onClick={() => setIsDeleteDialogOpen(true)}
                  size="small"
                  sx={{ color: tokens.text.excerpt, p: 0.5 }}
                >
                  <img src={DeleteIcon} alt="Delete" style={{ width: 18, height: 18 }} />
                </IconButton>
                <IconButton
                  onClick={() => setSelectedScenario(null)}
                  size="small"
                  sx={{ color: tokens.text.excerpt, p: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          </Paper>

          {/* Recommendations */}
          <ScenarioDetail
            id="scenario-detail"
            scenario={selectedScenarioData}
            metrics={metricsData?.calculateScenarioMetrics}
            isLoadingMetrics={isLoadingMetrics}
            onClose={() => setSelectedScenario(null)}
            onDeleted={handleScenarioDeleted}
            onMetricsUpdate={() => refetchMetrics()}
            isCompact={true}
            isSlim={false}
            showOnlyRecommendations={true}
            hideButtons={true}
            isEditDialogOpen={isEditDialogOpen}
            setIsEditDialogOpen={setIsEditDialogOpen}
            isDeleteDialogOpen={isDeleteDialogOpen}
            setIsDeleteDialogOpen={setIsDeleteDialogOpen}
          />
        </Box>
      )}
        </Box>
  );
};

export default ScenarioModelingPage;
